import React, { Component, PropTypes } from 'react';
import DropzoneComponent from 'react-dropzone-component';
import s3functions from '../../../utils/s3Upload';
import uuid from 'node-uuid';
import { LinearProgress } from 'material-ui';
import { call, cond, equals, T, contains } from 'ramda';
import FileError from './file-error';

class DropzoneWithPreview extends Component {

  constructor(props) {
    super(props);
    this.state = {
      files: this.props.allFiles,
      showErrorDialog: false,
      errorDialogType: '',
      problemFiles: [],
      uploadingFiles: false,
      percent: 0,
    };
  }

  componentDidMount() {
    window.addEventListener('beforeunload', (e) => this.handleWindowClose(e));
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleWindowClose());
  }

  handleWindowClose(e) {
    if (this.state.uploadingFiles) {
      const location = window.location.pathname;
      if (location === '/profile') {
        e.preventDefault();
        e.returnValue = ''; // doesn't matter what this string is, the browser will show it's own
      }
    }
  }

  render() {
    const componentConfig = {
      showFiletypeIcon: this.props.showFiletypeIcon,
      postUrl: 'no-url', // this is required and will return a 404
    };

    const djsConfig = {
      dictDefaultMessage: this.props.addFilesMessage,
      dictFileTooBig: `File is too big ({{filesize}}MB). Max filesize: ${this.props.maxFileSize}MB.`,
      addRemoveLinks: this.props.addRemoveLinks,
      acceptedFiles: this.props.acceptedFileTypes,
    };

    const onDrop = (file) => {
      const callback = this.props.finishedFileUploadCB;
      const showThumbnailPreviews = this.props.showThumbnailPreviews;
      const dropzone = this.dropzone;
      const flipLoadingPhotosState = (state) => this.setState({
        uploadingFiles: state,
        percent: 0,
      });
      if (file) {
        if (!contains(file.type, this.props.arrayOfAcceptedFileTypes)) {
          this.state.problemFiles.push(file);
          this.setState({
            showErrorDialog: true,
            errorDialogType: 'fileTypeError',
          });
          dropzone.removeFile(dropzone.files[0]);
        } else if (this.props.arrayOfPhotoTypes.indexOf(file.type) > -1 && Number(file.size.toFixed(3)) < (Number(this.props.suggestedMinFileSize * 1000).toFixed(3))) {
          this.state.problemFiles.push(file);
          this.setState({
            showErrorDialog: true,
            errorDialogType: 'lowResAlert',
          });
          dropzone.removeFile(dropzone.files[0]);
        } else if (Number(file.size) > Number(this.props.maxFileSize * 1000)) {
          this.state.problemFiles.push(file);
          this.setState({
            showErrorDialog: true,
            errorDialogType: 'fileTooBig',
          });
          dropzone.removeFile(dropzone.files[0]);
        } else {
          this.setState({ uploadingFiles: true });
          return s3functions.s3upload({
            file_to_upload: file,
            file_name: `general-photos/${uuid.v1()}`,
            onProgress(percent) {
              if (percent === 100) return flipLoadingPhotosState(false);
            },
            onFinishS3Put(publicUrl) {
              if (!showThumbnailPreviews && dropzone.files.length > 0) {
                dropzone.removeFile(dropzone.files[0]);
              }
              if (callback) return callback(publicUrl, file.type, file.name, (file.size / (1000 * 1000)).toFixed(3));
            },
            onError(status) {
              console.log(status);
            },
          });
        }
      }
    };

    const closeErrorDialog = () => this.setState({
      showErrorDialog: false,
      errorDialogType: '',
      problemFiles: [],
    });

    const getErrorMessage = () => call(cond([
      [equals('lowResAlert'), () => {
        return (
          <FileError
            showDialog
            title="Low Resolution Alert"
            confirmButtonFunc={closeErrorDialog}
            topText={<h5>
              The resolution of your photo is too low and may appear grainy or pixelated when printed.
              The suggested minimum file size is {this.props.suggestedMinFileSize / 1000}MB / {this.props.suggestedMinFileSize}KB.
              Please consider uploading {this.state.problemFiles.length === 1 ? 'a higher resolution file' : 'higher resolution files'} for the following:
            </h5>}
            problemFiles={this.state.problemFiles}
          />
        );
      }],
      [equals('fileTooBig'), () => {
        return (
          <FileError
            showDialog
            title="File Size Error"
            confirmButtonFunc={closeErrorDialog}
            topText={<h5>
              The max file size is {this.props.maxFileSize}MB.
              The following {this.state.problemFiles.length === 1 ? 'file is' : 'files are'} too large:
            </h5>}
            problemFiles={this.state.problemFiles}
            subtext={<h5 style={{ marginTop: '5px' }}>Please compress {this.state.problemFiles.length === 1 ? 'this file' : 'these files'} or upload an alternative.</h5>}
          />
        );
      }],
      [equals('fileTypeError'), () => {
        return (
          <FileError
            showDialog
            title="Unsupported File Type Error"
            confirmButtonFunc={closeErrorDialog}
            topText={<h5>
              Supported file types include: {this.props.acceptedFileTypes}.
              <br/>
              The following {this.state.problemFiles.length === 1 ? 'file is' : 'files are'} unsupported:
            </h5>}
            problemFiles={this.state.problemFiles}
          />
        );
      }],
      [T, () => {
        return (
          <FileError
            showDialog
            title="File Upload Error"
            confirmButtonFunc={closeErrorDialog}
            topText={<h5>
              Unable to upload the following {this.state.problemFiles.length === 1 ? 'file' : 'files'}:
            </h5>}
            problemFiles={this.state.problemFiles}
            subtext={<h5 style={{ marginTop: '5px' }}>Please try again.</h5>}
          />
        );
      }],
    ]), this.state.errorDialogType);

    const setDropzone = (dropzone) => this.dropzone = dropzone;

    const eventHandlers = {
      init: (passedDropzone) => setDropzone(passedDropzone),
      // All of these receive the event as first parameter:
      drop: null,
      dragstart: null,
      dragend: null,
      dragenter: null,
      dragover: null,
      dragleave: null,
      // All of these receive the file as first parameter:
      addedfile: onDrop,
      removedfile: null,
      thumbnail: null,
      error: null,
      processing: () => this.props.uploadingFile(),
      uploadprogress: null,
      sending: null,
      success: null,
      complete: null,
      canceled: null,
      maxfilesreached: null,
      maxfilesexceeded: null,
      // All of these receive a list of files as first parameter
      // and are only called if the uploadMultiple option
      // in djsConfig is true:
      processingmultiple: null,
      sendingmultiple: null,
      successmultiple: null,
      completemultiple: null,
      canceledmultiple: null,
      // Special Events
      totaluploadprogress: (p) => this.setState({ percent: Math.floor(p) }),
      reset: null,
      queuecompleted: null,
    };

    return (
      <div style={{ pointerEvents: this.state.uploadingFiles ? 'none' : '' }}>
        <DropzoneComponent
          config={componentConfig}
          eventHandlers={eventHandlers}
          djsConfig={djsConfig}
        />
        {
          this.state.uploadingFiles ?
            <LinearProgress mode="determinate" value={this.state.percent} /> : <span/>
        }
        {this.state.showErrorDialog ? getErrorMessage() : <span/>}
      </div>
    );
  }

}

DropzoneWithPreview.propTypes = {
  addRemoveLinks: PropTypes.bool, // show remove link under thumbnail
  acceptedFileTypes: PropTypes.string, // string of accepted file types like '.jpg, .png'
  showFiletypeIcon: PropTypes.bool, // show image of file types
  finishedFileUploadCB: PropTypes.func,
  allFiles: PropTypes.array,
  maxFileSize: PropTypes.number, // in MB
  showThumbnailPreviews: PropTypes.bool, // show thumbnails after upload complete
  uploadingFile: PropTypes.func,
  addFilesMessage: PropTypes.string,
  suggestedMinFileSize: PropTypes.string, // in KB
  arrayOfAcceptedFileTypes: PropTypes.array, // ['image/jpeg', 'image/png'] need this because file.accepted doesn't always work
  arrayOfPhotoTypes: PropTypes.array,
};

export default DropzoneWithPreview;



// WEBPACK FOOTER //
// ./src/app/components/extra/dropzone-with-preview/dropzone.js